import { DownOutlined } from "@ant-design/icons";
import { Collapse } from "antd";
import type { CollapseProps } from "antd";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFinancialAnalytics } from "../../../../../../../api/services/financial-integration.hooks";
import type { ShariahScreeningResponse } from "../../../../../../../api/services/shariah-screening.type";
import { normalizeHalalStatus } from "./shariahReportHalalStatus";
import styles from "./shariahReport.module.css";

const P = "pages.topHalal.info.shariahReport.";

interface FaqJsonLdItem {
  "@type": "Question";
  name: string;
  acceptedAnswer: {
    "@type": "Answer";
    text: string;
  };
}

interface ShariahReportFaqProps {
  screening?: ShariahScreeningResponse | null;
}

export function ShariahReportFaq({ screening }: ShariahReportFaqProps) {
  const { t } = useTranslation();
  const { symbol: symbolParam = "" } = useParams<{ symbol: string }>();
  const { data } = useFinancialAnalytics(
    { symbol: symbolParam },
    { enabled: Boolean(symbolParam) && !screening },
  );

  const analytics = data?.data;
  const companyProfile = analytics?.companyProfile;
  const symbol = (
    screening?.ticker ??
    analytics?.symbol ??
    symbolParam
  ).toUpperCase();
  const company =
    screening?.company_name?.trim() ||
    companyProfile?.companyName?.trim() ||
    analytics?.symbol ||
    symbolParam ||
    "—";
  const rawStatus = screening?.halal_status ?? analytics?.halal_status;
  const normalized = normalizeHalalStatus(rawStatus);

  const statusLabel = useMemo(() => {
    switch (normalized) {
      case "halal":
        return t(`${P}halal`);
      case "doubtful":
        return t(`${P}doubtful`);
      case "not_halal":
        return t(`${P}notHalal`);
      case "unqualified":
        return t(`${P}notQualified`);
      default:
        return t(`${P}faq.statusUnknown`);
    }
  }, [normalized, t]);

  const vars = useMemo(
    () => ({
      symbol,
      company,
      statusLabel,
    }),
    [symbol, company, statusLabel],
  );

  const faqItems = useMemo(() => {
    const q4Key =
      normalized === "unknown" ? "faq.q4Pending" : "faq.q4";
    const a4Key =
      normalized === "unknown" ? "faq.a4Pending" : "faq.a4";
    return [
      { q: t(`${P}faq.q1`, vars), a: t(`${P}faq.a1`, vars) },
      { q: t(`${P}faq.q2`, vars), a: t(`${P}faq.a2`, vars) },
      { q: t(`${P}faq.q3`, vars), a: t(`${P}faq.a3`, vars) },
      { q: t(`${P}${q4Key}`, vars), a: t(`${P}${a4Key}`, vars) },
      { q: t(`${P}faq.q5`, vars), a: t(`${P}faq.a5`, vars) },
    ];
  }, [normalized, t, vars]);

  const jsonLd = useMemo(() => {
    const mainEntity: FaqJsonLdItem[] = faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    }));
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity,
    };
  }, [faqItems]);

  const collapseItems: CollapseProps["items"] = useMemo(
    () =>
      faqItems.map((item, index) => ({
        key: String(index + 1),
        label: (
          <span className={styles.faqPanelLabel}>
            {index + 1}. {item.q}
          </span>
        ),
        children: <p className={styles.faqAnswer}>{item.a}</p>,
        // Closed answers stay in the HTML (hidden), so search engines read
        // them and they match the FAQPage JSON-LD below.
        forceRender: true,
      })),
    [faqItems],
  );

  return (
    <section
      className={styles.faqSection}
      id="shariah-stock-faq"
      aria-labelledby="shariah-faq-heading"
    >
      <h2 id="shariah-faq-heading" className={styles.faqHeading}>
        {t(`${P}faq.title`)}
      </h2>
      <p className={styles.faqIntro}>{t(`${P}faq.intro`, vars)}</p>
      <Collapse
        accordion
        className={styles.faqCollapse}
        bordered={false}
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <span
            className={`${styles.faqExpandIconWrap} ${isActive ? styles.faqExpandIconOpen : ""}`}
            aria-hidden
          >
            <DownOutlined className={styles.faqDownIcon} />
          </span>
        )}
        items={collapseItems}
      />
      <script
        type="application/ld+json"
        // Escape < so answers cannot break out of </script> if text ever contains it.
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
        }}
      />
    </section>
  );
}
